"use client";

import { useSession } from 'next-auth/react';
import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Logger } from '@/lib/logger';
import type { OrgType } from '@/lib/types/org';
import { AUTH_API } from '@/lib/UrlConstants';

interface SessionContextData {
  orgId: string | null;
  userId: string | null;
  orgType: OrgType | null;
  orgName?: string | null;
  role?: string | null;
  isLoading: boolean;
  error: string | null;
  isEnhanced: boolean;
  retry: () => void;
}

// Client-side business context cache
const CACHE_KEY = 'business_context';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedContext {
  orgId: string;
  userId: string;
  orgType: OrgType | null;
  orgName?: string;
  role?: string;
  expiry: number;
}

export function useAuth(): SessionContextData {
  const { data: session, status } = useSession();
  const router = useRouter();
  const [contextData, setContextData] = useState<SessionContextData>({
    orgId: null,
    userId: null,
    orgType: null,
    isLoading: true,
    error: null,
    isEnhanced: false,
    retry: () => {}
  });

  // Helper function to handle session expiration/authentication errors
  const handleAuthenticationError = useCallback((error: string, shouldRedirect = true) => {
    Logger.warn('useAuth', 'Authentication error detected, clearing cache and redirecting', {
      error,
      shouldRedirect
    });
    
    // Clear any cached context
    if (typeof window !== 'undefined') {
      try {
        // Clear sessionStorage
        window.sessionStorage.clear();
        // Clear localStorage business context cache
        Object.keys(window.localStorage).forEach(key => {
          if (key.startsWith(CACHE_KEY)) {
            window.localStorage.removeItem(key);
          }
        });
      } catch (err) {
        Logger.warn('useAuth', 'Failed to clear cache during auth error', err);
      }
    }
    
    if (shouldRedirect) {
      // Add a small delay to prevent rapid redirects
      setTimeout(() => {
        router.replace('/login?reason=session_expired');
      }, 100);
    }
  }, [router]);

  const enhanceSession = useCallback(async (force = false) => {
    // Check if session is still loading
    if (status === 'loading') {
      Logger.info('useAuth', 'Session still loading, waiting...');
      return; // Don't set error state if still loading
    }
    
    // More robust session validation
    if (!session?.user?.id) {
      Logger.warn('useAuth', 'No valid session found for enhancement', {
        hasSession: !!session,
        hasUser: !!session?.user,
        hasUserId: !!session?.user?.id,
        status
      });
      
      // Set temporary error state with automatic retry
      setContextData(prev => ({
        ...prev,
        isLoading: false,
        error: 'No valid session found',
        isEnhanced: false,
        retry: () => enhanceSession(true)
      }));
      
      // Auto-retry after a short delay in case this is a temporary race condition
      setTimeout(() => {
        if (status === 'authenticated' && session?.user?.id) {
          Logger.info('useAuth', 'Auto-retrying session enhancement after delay');
          enhanceSession(true);
        }
      }, 1000); // 1 second delay
      
      return;
    }

    const userId = session.user.id;

    try {
      // Check if JWT already has business context
      if (session.orgId && session.hasBusinessContext && !force) {
        Logger.info('useAuth', 'Using JWT business context', {
          userId,
          orgId: session.orgId,
          orgType: session.orgType
        });

        setContextData({
          orgId: session.orgId,
          userId,
          orgType: (session.orgType as OrgType) || null,
          orgName: session.orgName,
          role: session.role,
          isLoading: false,
          error: null,
          isEnhanced: true,
          retry: () => enhanceSession(true)
        });
        return;
      }

      // Check localStorage cache
      const cached = getCachedContext(userId);
      if (cached && !force) {
        Logger.info('useAuth', 'Using cached business context', {
          userId,
          orgId: cached.orgId
        });

        setContextData({
          orgId: cached.orgId,
          userId,
          orgType: cached.orgType,
          orgName: cached.orgName,
          role: cached.role,
          isLoading: false,
          error: null,
          isEnhanced: true,
          retry: () => enhanceSession(true)
        });
        return;
      }

      // Fetch from enhancement API
      Logger.info('useAuth', 'Fetching business context from API', { userId });
      
      const response = await fetch(AUTH_API.ENHANCE_SESSION, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Enhancement failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.businessContext?.orgId) {
        const context = data.businessContext;
        
        // Cache the successful result
        setCachedContext(userId, {
          orgId: context.orgId,
          orgType: context.orgType,
          orgName: context.orgName,
          role: context.role
        });

        setContextData({
          orgId: context.orgId,
          userId,
          orgType: context.orgType || null,
          orgName: context.orgName,
          role: context.role,
          isLoading: false,
          error: null,
          isEnhanced: true,
          retry: () => enhanceSession(true)
        });

        Logger.info('useAuth', 'Session enhanced successfully', {
          userId,
          orgId: context.orgId,
          orgType: context.orgType
        });
      } else {
        throw new Error(data.error || 'Enhancement returned no organization data');
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      Logger.apiError('useAuth', 'Session enhancement failed', error, {
        method: 'GET',
        action: 'enhanceSession',
        requestId: Math.random().toString(36).substring(2, 12),
        userId,
        error: errorMessage
      });

      // Check if this is an authentication/session error that requires login redirect
      const isAuthError = errorMessage.includes('Invalid session') ||
                         errorMessage.includes('401') ||
                         errorMessage.includes('Unauthorized') ||
                         errorMessage.includes('Authentication required') ||
                         errorMessage.includes('Session expired') ||
                         errorMessage.includes('Please log in again');

      if (isAuthError) {
        handleAuthenticationError(errorMessage);
        return; // Don't set context data as we're redirecting
      }

      // For other errors (like organization context issues), show error with retry
      setContextData({
        orgId: null,
        userId,
        orgType: null,
        isLoading: false,
        error: errorMessage.includes('Enhancement failed: 401') 
                ? 'Session expired. Please log in again.'
                : errorMessage,
        isEnhanced: false,
        retry: () => enhanceSession(true)
      });
    }
  }, [session]);

  useEffect(() => {
    if (status === 'loading') {
      Logger.info('useAuth', 'Session status is loading, waiting for completion');
      return; // Wait for session to load
    }

    if (status === 'unauthenticated') {
      Logger.info('useAuth', 'User not authenticated, redirecting to login');
      handleAuthenticationError('User not authenticated');
      return;
    }

    // Additional validation to ensure session is fully loaded
    if (status === 'authenticated' && session?.user?.id) {
      Logger.info('useAuth', 'Session authenticated, triggering enhancement', {
        userId: session.user.id,
        hasOrgId: !!session.orgId,
        hasBusinessContext: !!session.hasBusinessContext
      });
      
      // Small delay to ensure session is fully populated
      const timer = setTimeout(() => {
        enhanceSession();
      }, 100);
      
      return () => clearTimeout(timer);
    } else if (status === 'authenticated') {
      Logger.warn('useAuth', 'Session authenticated but missing user data', {
        hasSession: !!session,
        hasUser: !!session?.user,
        hasUserId: !!session?.user?.id
      });
    }
  }, [status, session?.user?.id, session?.orgId, session?.hasBusinessContext, enhanceSession]);

  return contextData;
}

// Cache management functions
function getCachedContext(userId: string): CachedContext | null {
  try {
    const cached = localStorage.getItem(`${CACHE_KEY}_${userId}`);
    if (!cached) return null;

    const parsed: CachedContext = JSON.parse(cached);
    if (parsed.expiry < Date.now()) {
      localStorage.removeItem(`${CACHE_KEY}_${userId}`);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function setCachedContext(userId: string, context: Omit<CachedContext, 'userId' | 'expiry'>): void {
  try {
    const cacheData: CachedContext = {
      ...context,
      userId,
      expiry: Date.now() + CACHE_TTL
    };
    localStorage.setItem(`${CACHE_KEY}_${userId}`, JSON.stringify(cacheData));
  } catch (error) {
    Logger.warn('useAuth', 'Failed to cache context', { userId, error });
  }
}

export function clearSessionContextCache(userId?: string): void {
  try {
    if (userId) {
      localStorage.removeItem(`${CACHE_KEY}_${userId}`);
    } else {
      // Clear all session-related cache
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(CACHE_KEY)) {
          localStorage.removeItem(key);
        }
      });
    }
  } catch (error) {
    Logger.warn('useAuth', 'Failed to clear cache', { error });
  }
}
